// Translated
// Migrated
<template>
  <div
    class="py-4 room-container flex -mx-3 px-2 md:px-3"
    :class="{ 'room-container__inactive': isInactive }"
  >
    <div class="flex flex-col items-center mr-2">
      <fa
        class="text-white bg-blue rounded-full p-2"
        :icon="faBedFront"
        style="font-size: 15px"
      />
      <div class="h-full bg-gray w-[1px]" />
    </div>
    <div class="w-full flex flex-col md:flex-row justify-between">
      <div class="flex flex-col grow">
        <div class="py-1 pl-1 pr-4 font-medium flex items-center justify-between w-full">
          <h5 class="my-0">
            {{ roomType }}
          </h5>
          <span v-if="hasMultipleRoomOptions && selection">
            <a
              class="link-underline text-black"
              data-i18n="hotelDeselectRoom"
              @click.stop="$emit('select-room', roomGroupIndex, null, null)"
            >{{ $t('hotelDeselectRoom') }}</a>
          </span>
        </div>
        <div
          v-for="{ extra, id, MealBasis, supplier, ...room } in roomsToDisplay"
          :key="`room-line-${id}`"
          class="room-line"
          :class="{ active: selection === id }"
          @click="$emit('select-room', roomGroupIndex, id, roomIndex)"
        >
          <div>
            <input
              v-model="selection"
              type="radio"
              :name="`room-id-${roomIndex}__${id}`"
              :value="id"
            />
            <label class="pt-[5px]">{{ MealBasis }}
              <template v-if="showOfficeTools">
                <span
                  v-if="room?.room_is_on_request"
                  class="text-bright-red ml-2"
                  data-i18n="roomIsOnRequest"
                >{{ $t('roomIsOnRequest') }}</span>
                <span class="small ml-2 inline-block text-gray">
                  {{ supplier }}
                </span>
              </template>
            </label>
          </div>
          <template v-if="extra > 0 || roomGroupLength > 1">
            <i18n-t
              class="pr-1"
              data-i18n="perRoomFormat"
              keypath="perRoomFormat"
              tag="div"
            >
              <template #price>
                <strong>
                  {{ $n(extra) }}
                </strong>
              </template>
              <template #perRoom>
                {{ $t('perRoom') }}
              </template>
            </i18n-t>
          </template>
        </div>
        <div
          v-if="roomFacilities"
          class="hotel-item__facilities mt-1 pl-1"
        >
          <span
            class="block small font-bold"
            data-i18n="hotelFacilities"
          >{{ $t('facilities') }}</span>
          <span
            class="small"
            v-html="roomFacilities"
          />
          <br />
          <div
            v-if="firstRoom.facilities.length > facilitiesCollapsedLength"
            class="small"
          >
            <u
              class="cursor-pointer"
              @click="facilitiesExpanded = !facilitiesExpanded"
            >{{ facilitiesExpanded ? $t('showLess') : $t('showMoreButton') }}</u>
          </div>
        </div>
        <div
          v-if="erratum"
          class="hotel-item__facilities mt-1 pl-1 hotel-item__erratum"
        >
          <div
            v-for="({ Subject, Description }, eroomIndex) in erratum"
            :key="`errata-${roomGroupIndex}-${eroomIndex}`"
            class="p-2"
          >
            <span class="small font-bold">
              {{ Subject }}
            </span>
            <span class="small ml-2">
              {{ Description }}
            </span>
          </div>
        </div>
      </div>
      <div
        v-if="images?.length"
        class="md:min-w-[6rem] md:max-h-[6rem] mt-4 md:mt-0 cursor-pointer"
        @click="$emit('open-gallery', galleryImages)"
      >
        <LazyResponsiveHotelImage
          class="product-content__hotel-image hotel-read-mored block bg-center"
          v-bind="primaryImage"
          :in-view-scroll-target="scrollTarget"
          alt="Room images"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia'
import { faCamera, faBedFront } from '@fortawesome/pro-solid-svg-icons'
import { supplierURLs } from '@packages/media/mixins/hotel-images.mixin'

import charterHotelMixin from '@layers/web/mixins/charter-hotel.mixin'

export default defineNuxtComponent({
  mixins: [charterHotelMixin],

  props: {
    room: {
      type: Array,
      default: () => [],
    },

    roomIndex: {
      type: [String, Number],
      default: '',
    },

    roomGroupIndex: {
      type: Number,
      default: 0,
    },

    roomGroupLength: {
      type: Number,
      default: 0,
    },

    hasMultipleRoomOptions: {
      type: Boolean,
      default: false,
    },

    selection: {
      type: String,
      default: '',
    },

    isInactive: {
      type: Boolean,
      default: false,
    },

    scrollTarget: {
      type: htmlTypes.HTMLElement,
      default: undefined,
    },
  },

  emits: ['select-room', 'open-gallery'],

  data () {
    return {
      faCamera,
      faBedFront,
      facilitiesCollapsedLength: 3,
      facilitiesExpanded: false,
    }
  },

  computed: {
    ...mapState(useRootStore, {
      showOfficeTools: state => state.showOfficeTools,
    }),

    firstRoom () {
      return this.room[0] || {}
    },

    roomType () {
      return this.firstRoom?.RoomType || ''
    },

    images () {
      return this.firstRoom?.images || []
    },

    erratum () {
      return this.firstRoom?.Errata?.Erratum
    },

    roomsToDisplay () {
      return this.room.filter(({ id }) => !this.selection || this.selection === id)
    },

    primaryImage () {
      return this.images[0]
        ? this.images[0]
        // from the mixin
        : this.featuredImg
    },

    galleryImages () {
      if (this.images.length > 0) {
        return this.images.map(i => supplierURLs[i.type].replace('[PATH]', i.image))
      }

      return [this.autoImageSize(this.featuredImg)]
    },

    roomFacilities () {
      const room = this.firstRoom
      if (room.facilities.length) {
        return room.facilities
          .slice(0, this.facilitiesExpanded ? room.facilities.length : this.facilitiesCollapsedLength)
          .map(({ facility_name: facilityName, number }) => facilityName + (number ? `: ${number}` : ''))
          .join(' &middot; ')
      }

      return ''
    },
  },
})
</script>
